import { html, LitElement, customElement } from './lit-global.js';
import styles from './m-ico.css';

interface MicoSvg {
	value: string;
	viewBox: string;
	stroke?: string | null;
	errorCode?: number;
	errorMessage?: string | null;
	warningMessage?: string | null;
	resolvedFile?: string | null;
}

export function resolveMicoFile(file: string, type = 'svg', folder = 'lucide') {
	if (!file) return '';
	if (file.includes(':')) return file;
	const path = file.includes('/') ? file : `${folder || 'lucide'}/${file}`;
	return `${type || 'svg'}:${path}`;
}

@customElement('m-ico')
export class Mico extends LitElement {
	file!: string;
	type!: string;
	folder!: string;
	svg: MicoSvg | null = null;
	color!: string;
	fill!: string;
	scale!: number;
	stroke!: number;
	rotate!: number;
	background!: string;
	styleText!: string;
	//className!: string;
	//iconClassName!: string;

	static properties = {
		file: { type: String, attribute: "file" },
		type: { type: String, attribute: "type" },
		folder: { type: String, attribute: "folder" },
		color: { type: String, attribute: "color"  },
		fill: { type: String, attribute: "fill"  },
		scale: { type: Number, attribute: "scale"  },
		stroke: { type: Number, attribute: "stroke" },
		rotate: { type: Number, attribute: "rotate" },
		background: { type: String, attribute: "background" },
		//iconClassName: { type: String, attribute: "icon-class" },
		styleText: { type: String, attribute: "style" },
	};

	static styles = styles;
	private static warnedSvgMessages = new Set<string>();
	//createRenderRoot() { return this; }

	connectedCallback() {
		super.connectedCallback();
		if (!this.hasAttribute('aria-hidden')) {
			this.setAttribute('aria-hidden', 'true');
		}
		if (!this.hasAttribute('type')) {
			this.type = 'svg';
		}
		if (!this.hasAttribute('folder')) {
			this.folder = 'lucide';
		}
	}

	async loadSvg() {
		const file = this.getResolvedFile();
		if (!file || !file.startsWith("svg:")) {
			this.svg = null;
			return;
		}
		const cacheKey = file;

		let call = Modelyn.moduleCache.get(cacheKey) as Promise<MicoSvg | null> | undefined;
		if (!call) {
			call = Mico.fetchSvg('/mdln-api/mico?file=' + encodeURIComponent(file.substring(4)));
			Modelyn.moduleCache.set(cacheKey, call);
		}
		this.svg = await call;
		Mico.warnSvg(this.svg?.warningMessage);
	}

	private static async fetchSvg(url: string): Promise<MicoSvg | null> {
		try {
			const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
			const text = await response.text();
			const svg = text ? JSON.parse(text) as MicoSvg : null;

			if (!response.ok && !svg?.errorCode) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			return svg;
		}
		catch (ex) {
			console.warn('Unable to load m-ico SVG', ex);
			return null;
		}
	}

	private static warnSvg(message?: string | null) {
		if (!message || !Modelyn.isDebugMode || Mico.warnedSvgMessages.has(message)) return;
		Mico.warnedSvgMessages.add(message);
		console.warn(message);
	}

	getResolvedFile() {
		return resolveMicoFile(this.file, this.type, this.folder);
	}

	async performUpdate() {
		//this.iconClassName = this.className;
		//this.removeAttribute('class');

		await this.loadSvg();

		super.performUpdate();
	}

	render() {
		if (!this.svg) return html``;		

		const styles = [
			this.color != null ? `--mico-color:${this.color}` : '',
			this.fill != null ? `--mico-color-fill:${this.fill}` : '',			
			this.scale != null ? `--mico-scale:${this.scale}` : '',
			this.stroke != null ? `--mico-stroke-width:${this.stroke}` : '',
			this.rotate != null ? `--mico-rotate:${this.rotate}deg` : '',
			this.styleText || '',
		].filter(Boolean).join(';');
		const backgroundStyle = this.background != null ? `background-color:${this.background}` : '';

		//${this.iconClassName}
		return html`<span class="mdln-mico__surface" style=${backgroundStyle}>
			<svg class="mico" viewBox="${this.svg.viewBox}" stroke="${this.svg.stroke}" style=${styles}></svg>
		</span>`;
	}

	updated() {
		const el = this.renderRoot?.querySelector('svg.mico');
		if (!el || !this.svg) return;
		if (el.innerHTML !== this.svg.value) {
			el.innerHTML = this.svg.value;
		}
	}
}
